import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import {
  crmErrorText,
  crmInboxConversations,
  crmInboxMessages,
  crmInboxBotSettings,
  crmInboxCredentials,
  crmInboxPause,
  crmInboxSend,
  crmInboxSendMedia,
  crmInboxMediaBlobUrl,
  crmInboxMediaStreamUrl,
  crmInboxSendTest,
  crmInboxStatus,
  crmInboxSync,
  crmWhatsAppConnectUrl,
} from "../../../../api/crmApi";
import getSignalRService from "../../../../services/signalRService";
import { AttendBar, PrimaryBtn } from "./crmShared";
import { clearWhatsAppInboxUnread } from "./whatsAppInboxBadge";

function WhatsAppMark({ className = "" }) {
  return (
    <span className={`wa-channel-pill ${className}`.trim()} title="WhatsApp">
      <svg viewBox="0 0 24 24" aria-hidden="true" className="wa-channel-icon">
        <path
          fill="currentColor"
          d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 1.82c4.46 0 8.09 3.63 8.09 8.09 0 4.46-3.63 8.09-8.09 8.09-1.42 0-2.81-.37-4.03-1.07l-.29-.17-3.12.82.83-3.04-.19-.31a8.05 8.05 0 0 1-1.22-4.32c0-4.46 3.63-8.09 8.02-8.09zm4.43 10.53c-.24-.12-1.41-.7-1.63-.78-.22-.08-.38-.12-.54.12-.16.24-.62.78-.76.94-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.92-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.48-.39-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2 0 1.18.86 2.32.98 2.48.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.16 1.52.1.46-.07 1.41-.58 1.61-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28z"
        />
      </svg>
      WhatsApp
    </span>
  );
}

function pick(row, ...keys) {
  if (!row) return "";
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== "") return value;
  }
  return "";
}

function rowId(row) {
  return Number(pick(row, "id", "Id") || 0) || 0;
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function collapseBody(text) {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function isOutbound(msg) {
  const dir = String(pick(msg, "direction", "Direction")).toLowerCase();
  if (dir.startsWith("out") || dir === "sent" || dir === "bot") return true;
  if (dir.startsWith("in") || dir === "received") return false;
  return Boolean(
    msg?.isOutbound || msg?.IsOutbound || msg?.fromMe || msg?.FromMe,
  );
}

function messageType(msg) {
  return String(pick(msg, "messageType", "MessageType") || "text").toLowerCase();
}

function typeLabel(type) {
  switch (type) {
    case "image":
    case "sticker":
      return "Photo";
    case "video":
      return "Video";
    case "audio":
    case "voice":
    case "ptt":
      return "Audio";
    case "document":
      return "Document";
    case "location":
      return "Location";
    case "contacts":
    case "contact":
      return "Contact";
    case "poll":
      return "Poll";
    case "reaction":
      return "Reaction";
    default:
      return "Attachment";
  }
}

function hasMediaFile(msg) {
  return Boolean(pick(msg, "mediaUrl", "MediaUrl"));
}

function isMediaMessageType(type) {
  return ["image", "sticker", "video", "audio", "voice", "ptt", "document"].includes(type);
}

function isSpecialAttachment(type) {
  return ["location", "contacts", "contact", "poll", "reaction"].includes(type);
}

function mapsLinkFromBody(body) {
  const text = String(body || "");
  const match = text.match(/https:\/\/maps\.google\.com\/\?q=[^\s]+/i);
  return match ? match[0] : "";
}

function MediaLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="wa-media-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo"
      onClick={onClose}
    >
      <button
        type="button"
        className="wa-media-lightbox-close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
      <img
        src={src}
        alt={alt || "Photo"}
        className="wa-media-lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}

function InboxMedia({ conversationId, msg, outbound }) {
  const [url, setUrl] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [playError, setPlayError] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const id = rowId(msg);
  const type = messageType(msg);
  const body = collapseBody(pick(msg, "body", "Body"));
  const label = body || typeLabel(type);
  const mute = outbound ? "text-[#667781]" : "text-stone-500";
  const link = outbound ? "text-orange-800 underline" : "text-orange-700 underline";
  const hasFile = hasMediaFile(msg);
  const streamable = type === "video" || type === "audio" || type === "voice" || type === "ptt";

  useEffect(() => {
    let alive = true;
    let created = "";
    setLoadError(false);
    setPlayError(false);
    setUrl("");
    if (!conversationId || !id || !hasFile) return undefined;

    // Video/audio: stream from API (range requests) — do not wait for a full blob download.
    if (streamable) {
      setUrl(crmInboxMediaStreamUrl(conversationId, id));
      return undefined;
    }

    crmInboxMediaBlobUrl(conversationId, id, type)
      .then((blobUrl) => {
        if (!alive) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        created = blobUrl;
        setUrl(blobUrl);
      })
      .catch(() => {
        if (alive) {
          setUrl("");
          setLoadError(true);
        }
      });
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [conversationId, id, msg, type, hasFile, streamable]);

  if (type === "location") {
    const href = mapsLinkFromBody(body);
    return (
      <div className="space-y-1">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${mute}`}>
          Location
        </span>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer" className={`block text-sm ${link}`}>
            Open in Maps
          </a>
        ) : null}
      </div>
    );
  }

  if (type === "contacts" || type === "contact" || type === "poll" || type === "reaction") {
    return (
      <span className={`text-[11px] font-semibold uppercase tracking-wide ${mute}`}>
        {typeLabel(type)}
      </span>
    );
  }

  if (!hasFile) {
    return (
      <p className={`text-xs ${mute}`}>
        {typeLabel(type)} · not saved — ask customer to resend
      </p>
    );
  }

  if (!url) {
    return (
      <p className={`text-xs ${mute}`}>
        {typeLabel(type)}
        {loadError ? " · unavailable (file missing on server)" : " · loading…"}
      </p>
    );
  }

  if (type === "image" || type === "sticker") {
    return (
      <>
        <button
          type="button"
          className="wa-media-thumb"
          onClick={() => setLightbox(true)}
          title="View full screen"
        >
          <img
            src={url}
            alt={label}
            className="wa-media-thumb-img"
          />
        </button>
        {lightbox ? (
          <MediaLightbox src={url} alt={label} onClose={() => setLightbox(false)} />
        ) : null}
      </>
    );
  }

  if (type === "video") {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        playsInline
        className="max-h-56 max-w-full rounded-lg bg-black/10"
        onError={() => setPlayError(true)}
      />
    );
  }

  if (type === "audio" || type === "voice" || type === "ptt") {
    return (
      <div className="wa-audio-player space-y-1 min-w-[12rem]">
        <audio
          src={url}
          controls
          preload="metadata"
          className="w-full max-w-xs"
          onError={() => setPlayError(true)}
        />
        {playError ? (
          <a href={url} download={`voice-${id}.ogg`} className={`block text-xs ${link}`}>
            Download voice note
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-2 text-sm ${link}`}
    >
      Open {typeLabel(type).toLowerCase()}
    </a>
  );
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "WA";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

const CrmInbox = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [search, setSearch] = useState("");
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const fileRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [pulling, setPulling] = useState(false);
  const [syncNote, setSyncNote] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Hello from Sundarban CRM");
  const [testError, setTestError] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [credOpenAi, setCredOpenAi] = useState("");
  const [credModel, setCredModel] = useState("gpt-4o");
  const [credEndpoint, setCredEndpoint] = useState("https://api.openai.com");
  const [credDeployment, setCredDeployment] = useState("");
  const [credAppId, setCredAppId] = useState("");
  const [credAppSecret, setCredAppSecret] = useState("");
  const [credToken, setCredToken] = useState("");
  const [credWaba, setCredWaba] = useState("");
  const [credPhone, setCredPhone] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [credNote, setCredNote] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const threadRef = useRef(null);
  const didPull = useRef(false);
  const selectedIdRef = useRef(0);

  const connected = !!(status?.connected || status?.Connected);
  const connectUrl = crmWhatsAppConnectUrl();

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const loadStatus = useCallback(async () => {
    try {
      const data = await crmInboxStatus();
      setStatus(data);
      setStatusError("");
    } catch {
      setStatus({ connected: false, message: "Could not load inbox status." });
      setStatusError("Could not load inbox status.");
    }
  }, []);

  const loadConversations = useCallback(async () => {
    if (!connected) {
      setConversations([]);
      return;
    }
    try {
      const data = await crmInboxConversations({ search, page: 1, pageSize: 80 });
      setConversations(data.items);
    } catch {
      setConversations([]);
    }
  }, [connected, search]);

  const pullFromMeta = useCallback(async () => {
    if (!connected) return;
    setPulling(true);
    try {
      const data = await crmInboxSync();
      setConversations(data.items);
      const note = pick(data.sync, "message", "Message");
      setSyncNote(note || "Synced from Meta.");
    } catch {
      setSyncNote("Could not pull chats from Meta. Showing saved inbox.");
      await loadConversations();
    } finally {
      setPulling(false);
    }
  }, [connected, loadConversations]);

  const loadMessages = useCallback(async (id) => {
    if (!id) {
      setMessages([]);
      setSelected(null);
      return;
    }
    try {
      const [items, list] = await Promise.all([
        crmInboxMessages(id),
        crmInboxConversations({ page: 1, pageSize: 80 }),
      ]);
      setMessages(items);
      const match = (list.items || []).find((row) => rowId(row) === id) || null;
      setSelected(match);
    } catch {
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!status) return;
    setCredModel(pick(status, "openAiModel", "OpenAiModel") || "gpt-4o");
    setCredEndpoint(pick(status, "openAiEndpoint", "OpenAiEndpoint") || "https://api.openai.com");
    setCredDeployment(pick(status, "openAiDeployment", "OpenAiDeployment") || "");
    setCredAppId(pick(status, "appId", "AppId"));
    setCredWaba(pick(status, "wabaId", "WabaId"));
    setCredPhone(pick(status, "phoneNumberId", "PhoneNumberId"));
  }, [status]);

  useEffect(() => {
    loadConversations();
    // Light fallback only — live updates come from SignalR WhatsAppInbound.
    const timer = setInterval(loadConversations, 60000);
    return () => clearInterval(timer);
  }, [loadConversations]);

  useEffect(() => {
    const fromQuery = Number(searchParams.get("c") || 0);
    if (fromQuery > 0) setSelectedId(fromQuery);
  }, [searchParams]);

  useEffect(() => {
    clearWhatsAppInboxUnread();
  }, []);

  useEffect(() => {
    const refreshFromSignal = (payload) => {
      clearWhatsAppInboxUnread();
      const id = Number(
        payload?.conversationId ||
          payload?.ConversationId ||
          payload?.detail?.conversationId ||
          payload?.detail?.ConversationId ||
          0,
      );
      loadConversations();
      const openId = selectedIdRef.current;
      // Refresh the open thread when this event is for it (or unspecified).
      if (openId > 0 && (id === 0 || id === openId)) {
        loadMessages(openId);
      }
    };

    const onWindowInbound = (event) => refreshFromSignal(event?.detail || event);
    window.addEventListener("crm-whatsapp-inbound", onWindowInbound);

    const service = getSignalRService();
    const onHub = (payload) => refreshFromSignal(payload);
    service.on("WhatsAppInbound", onHub);
    service.on("WhatsAppEscalation", onHub);

    return () => {
      window.removeEventListener("crm-whatsapp-inbound", onWindowInbound);
      service.off("WhatsAppInbound", onHub);
      service.off("WhatsAppEscalation", onHub);
    };
  }, [loadConversations, loadMessages]);

  useEffect(() => {
    if (!connected || didPull.current) return undefined;
    didPull.current = true;
    pullFromMeta();
    return undefined;
  }, [connected, pullFromMeta]);

  useEffect(() => {
    if (!selectedId) return undefined;
    loadMessages(selectedId);
    // Rare fallback if SignalR drops — not every few seconds.
    const timer = setInterval(() => loadMessages(selectedId), 60000);
    return () => clearInterval(timer);
  }, [selectedId, loadMessages]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  const clearFile = () => {
    setFile(null);
    setFilePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const chooseFile = (picked) => {
    if (!picked) {
      clearFile();
      return;
    }
    setFile(picked);
    setFilePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      if (picked.type?.startsWith("image/")) return URL.createObjectURL(picked);
      if (picked.type?.startsWith("video/")) return URL.createObjectURL(picked);
      return "";
    });
  };

  const send = async (e) => {
    e.preventDefault();
    if (!selectedId || (!draft.trim() && !file)) return;
    setSending(true);
    setSendError("");
    try {
      const items = file
        ? await crmInboxSendMedia(selectedId, file, draft.trim())
        : await crmInboxSend(selectedId, draft.trim());
      setMessages(items);
      setDraft("");
      clearFile();
      await loadConversations();
    } catch (err) {
      setSendError(crmErrorText(err));
    } finally {
      setSending(false);
    }
  };

  const togglePause = async () => {
    if (!selectedId) return;
    const paused = pick(selected, "botPaused", "BotPaused") === true
      || pick(selected, "botPaused", "BotPaused") === "true";
    const updated = await crmInboxPause(selectedId, !paused);
    setSelected(updated);
    await loadConversations();
  };

  const saveCredentials = async (e) => {
    e.preventDefault();
    setSavingCreds(true);
    setCredNote("");
    try {
      await crmInboxCredentials({
        openAiApiKey: credOpenAi,
        openAiModel: credModel,
        openAiEndpoint: credEndpoint,
        openAiDeployment: credDeployment,
        appId: credAppId,
        appSecret: credAppSecret,
        accessToken: credToken,
        wabaId: credWaba || pick(status, "wabaId", "WabaId"),
        phoneNumberId: credPhone || pick(status, "phoneNumberId", "PhoneNumberId"),
      });
      setCredOpenAi("");
      setCredAppSecret("");
      setCredToken("");
      setCredNote("Saved. Other admins can use Inbox with these credentials.");
      await loadStatus();
    } catch (err) {
      setCredNote(crmErrorText(err));
    } finally {
      setSavingCreds(false);
    }
  };

  const toggleFollowUp = async () => {
    const enabled = !!(status?.followUpEnabled ?? status?.FollowUpEnabled);
    setSavingFollowUp(true);
    try {
      await crmInboxBotSettings({
        followUpEnabled: !enabled,
        followUpDelayMinutes: status?.followUpDelayMinutes || status?.FollowUpDelayMinutes || 2,
        followUpMaxCount: status?.followUpMaxCount || status?.FollowUpMaxCount || 2,
      });
      await loadStatus();
    } catch (err) {
      setStatusError(crmErrorText(err));
    } finally {
      setSavingFollowUp(false);
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTestSending(true);
    setTestError("");
    try {
      const result = await crmInboxSendTest(testTo, testBody);
      const id = Number(result.conversationId || result.ConversationId || 0);
      await loadConversations();
      if (id) setSelectedId(id);
    } catch (err) {
      setTestError(crmErrorText(err));
    } finally {
      setTestSending(false);
    }
  };

  const displayPhone = pick(status, "displayPhone", "DisplayPhone");
  const phoneNumberId = pick(status, "phoneNumberId", "PhoneNumberId");
  const wabaId = pick(status, "wabaId", "WabaId");
  const verifiedName = pick(status, "verifiedName", "VerifiedName");
  const statusMessage = pick(status, "message", "Message") || statusError;
  const tokenValid = status?.tokenValid ?? status?.TokenValid;
  const canSend = status?.canSend ?? status?.CanSend;
  const tokenMessage = pick(status, "tokenMessage", "TokenMessage");
  const botMode = pick(status, "botMode", "BotMode") || "scripted";
  const webhookPublicUrl = pick(status, "webhookPublicUrl", "WebhookPublicUrl");
  const followUpEnabled = !!(status?.followUpEnabled ?? status?.FollowUpEnabled);
  const canEditCredentials = !!(status?.canEditCredentials ?? status?.CanEditCredentials);
  const credentialsHint = pick(status, "credentialsHint", "CredentialsHint");
  const hasOpenAiKey = !!(status?.hasOpenAiKey ?? status?.HasOpenAiKey);
  const hasAppSecret = !!(status?.hasAppSecret ?? status?.HasAppSecret);
  const needsNewToken = connected && !(tokenValid && canSend);

  const paused =
    pick(selected, "botPaused", "BotPaused") === true ||
    pick(selected, "botPaused", "BotPaused") === "true";

  return (
    <Box top>
      <div className="crm-inbox">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <WhatsAppMark />
              <SetupTittle text="Inbox" />
            </div>
            <p className="text-sm text-stone-500 -mt-1">
              Customer chats from WhatsApp. Take over to reply as staff, or leave AI handling.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {connected ? (
              <span className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-stone-200 bg-white text-[11px] font-semibold text-stone-600">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                {verifiedName || displayPhone || "Connected"}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setSettingsOpen((open) => !open)}
              className={`h-9 px-3 rounded-lg border text-xs font-semibold ${
                settingsOpen
                  ? "border-stone-300 bg-stone-100 text-stone-800"
                  : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
              }`}
            >
              Settings
            </button>
            {connected ? (
              <button
                type="button"
                disabled={pulling}
                onClick={pullFromMeta}
                className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                {pulling ? "Refreshing…" : "Refresh from Meta"}
              </button>
            ) : null}
          </div>
        </div>

        {settingsOpen ? (
          connected ? (
            <div className="rounded-xl border border-stone-200 bg-white px-3 py-3 mb-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div>
                  <p className="text-sm font-semibold text-stone-800">Inbox settings</p>
                  <p className="text-[11px] text-stone-500">
                    WhatsApp connection, assistant options, and credentials.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(false)}
                  className="h-8 px-2.5 rounded-lg border border-stone-200 text-[11px] font-semibold text-stone-600 hover:bg-stone-50"
                >
                  Close
                </button>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-emerald-900">
                  <span className="inline-flex items-center gap-1.5 font-semibold">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Connected
                  </span>
                  <span>{verifiedName || "Test number"}</span>
                  <span>{displayPhone || ""}</span>
                  <span className="text-emerald-800">
                    Assistant {botMode === "openai" ? "GPT" : "live"}
                  </span>
                  {canEditCredentials ? (
                    <a
                      href={connectUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto font-semibold text-emerald-800 hover:underline"
                    >
                      Reconnect
                    </a>
                  ) : (
                    <span className="ml-auto text-[11px] text-emerald-800">Using saved credentials</span>
                  )}
                </div>
                {tokenMessage && !(tokenValid && canSend) ? (
                  <p className="text-xs text-red-700 mt-1">{tokenMessage}</p>
                ) : null}
                {syncNote ? <p className="text-[11px] text-emerald-800 mt-1">{syncNote}</p> : null}
                {credentialsHint ? <p className="text-[11px] text-emerald-800 mt-1">{credentialsHint}</p> : null}
                <label className="mt-2 flex items-start gap-2 text-xs text-emerald-900 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={followUpEnabled}
                    disabled={savingFollowUp}
                    onChange={toggleFollowUp}
                  />
                  <span>
                    <span className="font-semibold">Ask for feedback if the customer goes quiet</span>
                    <span className="block text-[11px] text-emerald-800">
                      Off by default. When on, the bot politely checks twice during a quote — not after general questions.
                    </span>
                  </span>
                </label>
                {canEditCredentials ? (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[11px] font-semibold text-emerald-800">
                      Change WhatsApp / OpenAI credentials
                    </summary>
                    <form onSubmit={saveCredentials} className="mt-2 grid grid-cols-1 gap-2">
                      <p className="text-[11px] text-emerald-800">
                        AI keys are stored encrypted in the database (not in appsettings). Leave blank to keep the saved key.
                        {needsNewToken
                          ? " This host cannot decrypt the saved Meta token, so paste a fresh Generate-token below."
                          : " Paste a Meta token only if WhatsApp send starts failing."}
                      </p>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        Azure / OpenAI key {hasOpenAiKey ? "(saved encrypted — leave blank to keep)" : "(required once)"}
                        <input
                          value={credOpenAi}
                          onChange={(e) => setCredOpenAi(e.target.value)}
                          placeholder={hasOpenAiKey ? "Leave blank to keep the saved key" : "Paste Azure OpenAI key here"}
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        Model / deployment name
                        <input
                          value={credModel}
                          onChange={(e) => setCredModel(e.target.value)}
                          placeholder="gpt-4o"
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        AI endpoint (OpenAI or Azure)
                        <input
                          value={credEndpoint}
                          onChange={(e) => setCredEndpoint(e.target.value)}
                          placeholder="https://YOUR-RESOURCE.openai.azure.com"
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        Azure deployment name (optional)
                        <input
                          value={credDeployment}
                          onChange={(e) => setCredDeployment(e.target.value)}
                          placeholder="Leave blank to use model name"
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        Meta App ID
                        <input
                          value={credAppId}
                          onChange={(e) => setCredAppId(e.target.value)}
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        Meta App Secret {hasAppSecret ? "(saved — leave blank to keep)" : ""}
                        <input
                          value={credAppSecret}
                          onChange={(e) => setCredAppSecret(e.target.value)}
                          placeholder={hasAppSecret ? "Leave blank to keep the saved secret" : "Meta App Secret"}
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <label className="text-[11px] font-semibold text-emerald-900">
                        Meta token {needsNewToken ? "(required on this host)" : "(optional)"}
                        <input
                          value={credToken}
                          onChange={(e) => setCredToken(e.target.value)}
                          placeholder={needsNewToken ? "Paste Generate token (starts with EAA)" : "Leave blank to keep the saved token"}
                          className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                        />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-[11px] font-semibold text-emerald-900">
                          WABA ID
                          <input
                            value={credWaba}
                            onChange={(e) => setCredWaba(e.target.value)}
                            className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                          />
                        </label>
                        <label className="text-[11px] font-semibold text-emerald-900">
                          Phone number ID
                          <input
                            value={credPhone}
                            onChange={(e) => setCredPhone(e.target.value)}
                            className="mt-1 w-full border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm font-normal"
                          />
                        </label>
                      </div>
                      <PrimaryBtn type="submit" disabled={savingCreds}>
                        {savingCreds ? "Saving…" : "Save credentials"}
                      </PrimaryBtn>
                      {credNote ? <p className="text-[11px] text-emerald-900">{credNote}</p> : null}
                    </form>
                  </details>
                ) : null}
                <details className="mt-2">
                  <summary className="cursor-pointer text-[11px] font-semibold text-emerald-800">
                    Send a test message
                  </summary>
                  <form onSubmit={sendTest} className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                    <input
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="WhatsApp number with country code"
                      className="border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm"
                    />
                    <input
                      value={testBody}
                      onChange={(e) => setTestBody(e.target.value)}
                      placeholder="Message"
                      className="border border-emerald-300 rounded-lg h-9 px-3 bg-white text-sm"
                    />
                    <PrimaryBtn type="submit" disabled={testSending || !testTo.trim() || !testBody.trim()}>
                      {testSending ? "Sending…" : "Send test"}
                    </PrimaryBtn>
                  </form>
                  {wabaId || phoneNumberId || webhookPublicUrl ? (
                    <p className="text-[11px] text-emerald-800 mt-2">
                      WABA {wabaId || "—"} · Phone ID {phoneNumberId || "—"}
                      {webhookPublicUrl ? ` · ${webhookPublicUrl}` : ""}
                    </p>
                  ) : null}
                  {testError ? <p className="text-xs text-red-600 mt-1">{testError}</p> : null}
                </details>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 mb-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="font-semibold text-sm">Inbox settings</p>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(false)}
                  className="h-8 px-2.5 rounded-lg border border-amber-200 text-[11px] font-semibold text-stone-600 hover:bg-amber-100"
                >
                  Close
                </button>
              </div>
              <p className="font-semibold text-sm mb-1">Cloud API is not connected yet</p>
              <p className="text-stone-700 text-sm leading-6">{statusMessage}</p>
              {canEditCredentials ? (
                <a
                  href={connectUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex mt-2 h-9 px-3 items-center rounded-lg bg-orange-600 text-white text-xs font-semibold"
                >
                  Open connect page
                </a>
              ) : (
                <p className="text-xs text-stone-600 mt-2">Ask the designated admin to connect WhatsApp once.</p>
              )}
            </div>
          )
        ) : !connected ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 mb-3">
            <p className="font-semibold text-sm mb-1">Cloud API is not connected yet</p>
            <p className="text-stone-700 text-sm leading-6">{statusMessage}</p>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="inline-flex mt-2 h-9 px-3 items-center rounded-lg border border-amber-300 bg-white text-xs font-semibold text-stone-700"
            >
              Open settings
            </button>
          </div>
        ) : null}

        {connected ? (
          <div className="crm-inbox-shell">
            <aside className="crm-inbox-pane bg-white rounded-xl border border-stone-200">
              <div className="p-3 border-b border-stone-200">
                <input
                  placeholder="Search name or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg h-9 px-3 bg-stone-50 text-sm"
                />
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {conversations.length === 0 ? (
                  <p className="text-sm text-stone-500 p-4">
                    No chats yet. Send a WhatsApp to the test number, then refresh.
                  </p>
                ) : (
                  conversations.map((row) => {
                    const id = rowId(row);
                    const active = id === selectedId;
                    const name =
                      pick(row, "profileName", "ProfileName") ||
                      pick(row, "phone", "Phone") ||
                      pick(row, "waId", "WaId") ||
                      "Chat";
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setSelectedId(id);
                          clearFile();
                          setSendError("");
                        }}
                        className={`w-full text-left px-3 py-2.5 border-b border-stone-100 flex gap-3 ${
                          active ? "bg-orange-50" : "hover:bg-stone-50"
                        }`}
                      >
                        <span className="h-9 w-9 shrink-0 rounded-full bg-stone-200 text-stone-700 text-xs font-bold flex items-center justify-center">
                          {initials(name)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-sm text-stone-900 truncate">
                              {name}
                            </span>
                            <span className="text-[10px] text-stone-400 shrink-0">
                              {formatWhen(pick(row, "lastMessageAt", "LastMessageAt"))}
                            </span>
                          </span>
                          <span className="flex items-center gap-1.5 mt-0.5">
                            <WhatsAppMark className="wa-channel-pill--xs" />
                            <span className="block text-xs text-stone-500 truncate min-w-0">
                              {pick(row, "lastBody", "LastBody") || "No messages"}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            <section className="crm-inbox-pane bg-white rounded-xl border border-stone-200">
              {!selectedId ? (
                <div className="flex-1 flex items-center justify-center p-6 text-sm text-stone-500">
                  Select a conversation
                </div>
              ) : (
                <>
                  <div className="px-4 py-3 border-b border-stone-200 shrink-0 wa-thread-header">
                    <div className="flex items-center gap-3">
                      <span className="h-9 w-9 rounded-full bg-[#25D366]/15 text-[#128C7E] text-xs font-bold flex items-center justify-center">
                        {initials(
                          pick(selected, "profileName", "ProfileName") || "WA",
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-semibold text-stone-900 truncate">
                            {pick(selected, "profileName", "ProfileName") ||
                              "WhatsApp chat"}
                          </p>
                          <WhatsAppMark className="wa-channel-pill--sm shrink-0" />
                        </div>
                        <p className="text-xs text-stone-500 truncate">
                          {pick(selected, "phone", "Phone") ||
                            pick(selected, "waId", "WaId")}
                          {pick(selected, "detectedLanguage", "DetectedLanguage")
                            ? ` · ${pick(selected, "detectedLanguage", "DetectedLanguage")}`
                            : ""}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {pick(selected, "queryId", "QueryId") ? (
                          <Link
                            to="/admin-dashboard/crm/queries"
                            className="h-8 px-3 rounded-lg border border-stone-200 text-xs font-semibold inline-flex items-center bg-white"
                          >
                            Lead #{pick(selected, "queryId", "QueryId")}
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          onClick={togglePause}
                          className="h-8 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold"
                        >
                          {paused ? "Let AI handle" : "Take over"}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <AttendBar
                        relatedType="conversation"
                        relatedId={selectedId}
                        phone={
                          pick(selected, "phone", "Phone") ||
                          pick(selected, "waId", "WaId")
                        }
                      />
                    </div>
                  </div>

                  <div
                    ref={threadRef}
                    className="crm-inbox-thread flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2"
                  >
                    {messages.length === 0 ? (
                      <p className="text-sm text-stone-500 text-center py-10">
                        No messages in this chat yet.
                      </p>
                    ) : (
                      messages.map((msg) => {
                        const outbound = isOutbound(msg);
                        const body = collapseBody(pick(msg, "body", "Body"));
                        const type = messageType(msg);
                        const media =
                          hasMediaFile(msg) ||
                          isMediaMessageType(type) ||
                          isSpecialAttachment(type);
                        const hideBody =
                          !body ||
                          body === typeLabel(type) ||
                          (type === "image" && body === "Photo") ||
                          (type === "sticker" && body === "Photo");
                        return (
                          <div
                            key={rowId(msg) || pick(msg, "creationDate", "CreationDate")}
                            className={`flex ${outbound ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`crm-inbox-bubble wa-bubble max-w-[min(72%,28rem)] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                                outbound
                                  ? "wa-bubble--out rounded-br-md"
                                  : "wa-bubble--in rounded-bl-md"
                              }`}
                            >
                              {!outbound ? (
                                <span className="wa-bubble-source">WhatsApp</span>
                              ) : null}
                              {media ? (
                                <div className="mb-1">
                                  <InboxMedia
                                    conversationId={selectedId}
                                    msg={msg}
                                    outbound={outbound}
                                  />
                                </div>
                              ) : null}
                              {body && !hideBody ? (
                                <p className="whitespace-pre-wrap break-words leading-5">
                                  {body}
                                </p>
                              ) : null}
                              <p
                                className={`text-[10px] mt-1 ${
                                  outbound ? "text-[#667781] text-right" : "text-stone-400"
                                }`}
                              >
                                {formatWhen(
                                  pick(msg, "creationDate", "CreationDate"),
                                )}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <form
                    onSubmit={send}
                    className="p-3 border-t border-stone-200 shrink-0 space-y-2"
                  >
                    {file ? (
                      <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-2 py-1.5">
                        {filePreview ? (
                          <img
                            src={filePreview}
                            alt=""
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <span className="text-[11px] font-semibold uppercase text-stone-500">
                            {file.type?.startsWith("audio/")
                              ? "Audio"
                              : file.type?.startsWith("video/")
                                ? "Video"
                                : "File"}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-xs text-stone-700">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={clearFile}
                          className="text-xs font-semibold text-stone-500 hover:text-stone-800"
                        >
                          Remove
                        </button>
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <input
                        ref={fileRef}
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/3gpp,audio/ogg,audio/mpeg,audio/mp4,audio/aac,audio/amr,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.webp,.gif,.mp4,.3gp,.ogg,.mp3,.m4a,.aac,.amr,.pdf"
                        onChange={(e) => chooseFile(e.target.files?.[0] || null)}
                      />
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="h-10 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold shrink-0"
                        title="Send photo, video, audio, or document"
                      >
                        Attach
                      </button>
                      <input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={file ? "Caption (optional)" : "Type a reply"}
                        className="flex-1 min-w-0 border border-stone-200 rounded-lg h-10 px-3 bg-white text-sm"
                      />
                      <PrimaryBtn
                        type="submit"
                        disabled={sending || (!draft.trim() && !file)}
                      >
                        Send
                      </PrimaryBtn>
                    </div>
                  </form>
                  {sendError ? (
                    <p className="text-xs text-red-600 px-3 pb-2">{String(sendError)}</p>
                  ) : null}
                </>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </Box>
  );
};

export default CrmInbox;
